import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Coins, Loader2, X } from "lucide-react";
import { grantFreeCredits } from "../../Services/GrowthService";

type CreditType = "INVITE" | "JOB" | "BOTH";

export interface CreditRecipient {
  userId: string;
  name: string;
  email: string | null;
  role?: string | null;
  inviteBalance: number | null;
  inviteTotal: number | null;
  jobCreditsBalance?: number | null;
}

interface GiveCreditsModalProps {
  recipient: CreditRecipient | null;
  onClose: () => void;
  onGranted: (message: string) => void;
  onError: (title: string, message: string) => void;
  /**
   * When true the grant closes the user's exhaustion alert, which is what moves them off the
   * Limits Exceeded list. The referral screen passes false because there is no alert to close
   * there and the wording would imply one exists.
   */
  closesAlert?: boolean;
}

const labelClass =
  "mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400";
const fieldClass =
  "w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]";

/**
 * One credit-grant dialog, shared by the referral tables and the Limits Exceeded list.
 *
 * <p>Three call sites that all do the same thing - raise a user's credit balance from the admin
 * side - with one copy of the validation and the current-balance readout. The amounts are additive,
 * which the helper text spells out because a balance that appears to be overwritten rather than
 * increased is the natural misreading of a field labelled "credits to add".
 */
const GiveCreditsModal = ({
  recipient,
  onClose,
  onGranted,
  onError,
  closesAlert = false,
}: GiveCreditsModalProps) => {
  const [creditType, setCreditType] = useState<CreditType>("INVITE");
  const [inviteCredits, setInviteCredits] = useState(5);
  const [jobCredits, setJobCredits] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  // Gated on the balance, not the role. A recruiter with no CompanyRecruiter row has no company to
  // credit, and the backend throws for exactly that case, so role alone would offer a JOB tab that
  // can only fail. Null balance means there is nothing here to top up.
  const needsJobTab = recipient?.jobCreditsBalance != null;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!recipient) return;

    const grantingInvite = creditType !== "JOB" && inviteCredits > 0;
    const grantingJob = creditType !== "INVITE" && jobCredits > 0;

    if (creditType === "INVITE" && inviteCredits <= 0) {
      onError("Invalid Amount", "Enter invite credits greater than zero.");
      return;
    }
    if (creditType === "JOB" && jobCredits <= 0) {
      onError("Invalid Amount", "Enter job credits greater than zero.");
      return;
    }
    if (creditType === "BOTH" && (inviteCredits <= 0 || jobCredits <= 0)) {
      onError("Invalid Amount", "Both invite and job credits must be greater than zero.");
      return;
    }
    if (!grantingInvite && !grantingJob) {
      onError("Invalid Amount", "Enter at least one credit amount greater than zero.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await grantFreeCredits({
        userId: recipient.userId,
        creditType,
        inviteCredits: grantingInvite ? inviteCredits : 0,
        jobCredits: grantingJob ? jobCredits : 0,
      });

      if (response.success) {
        onGranted(
          response.data?.message ||
            `${recipient.name}: credits added successfully.`
        );
        onClose();
      } else {
        onError("Grant Failed", response.message || "Please try again.");
      }
    } catch {
      onError("Grant Failed", "Failed to add credits. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {recipient && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-black text-slate-100">
                  <Coins size={18} className="text-amber-400" />
                  Give Credits
                </h2>
                <p className="text-xs font-semibold text-slate-400">
                  {recipient.name}
                  {recipient.email ? ` · ${recipient.email}` : ""}
                </p>
              </div>
              <button
                onClick={handleClose}
                disabled={submitting}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200 disabled:opacity-50"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mb-5 flex gap-2">
              {(["INVITE", ...(needsJobTab ? ["JOB" as const] : []), "BOTH"] as CreditType[]).map(
                (type) => (
                  <button
                    key={type}
                    onClick={() => setCreditType(type)}
                    className={`flex-1 rounded-xl border px-3 py-2.5 text-xs font-black uppercase tracking-wider transition ${
                      creditType === type
                        ? "border-amber-500/50 bg-amber-500/10 text-amber-400"
                        : "border-slate-800 bg-slate-950/60 text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    {type}
                  </button>
                )
              )}
            </div>

            <div className="space-y-4">
              {creditType !== "JOB" && (
                <div>
                  <label className={labelClass}>Invite Credits to Add</label>
                  <input
                    type="number"
                    min={1}
                    value={inviteCredits}
                    onChange={(e) => setInviteCredits(Number(e.target.value) || 0)}
                    className={fieldClass}
                  />
                  <p className="mt-1 text-[10px] font-semibold text-slate-500">
                    Current invite balance: {recipient.inviteBalance ?? 0} of{" "}
                    {recipient.inviteTotal ?? 0} granted
                  </p>
                  <p className="mt-0.5 text-[10px] font-semibold text-slate-600">
                    Adding credits raises both the balance and the granted total.
                  </p>
                </div>
              )}

              {creditType !== "INVITE" && (
                <div>
                  <label className={labelClass}>Job Credits to Add (recruiter)</label>
                  <input
                    type="number"
                    min={1}
                    value={jobCredits}
                    onChange={(e) => setJobCredits(Number(e.target.value) || 0)}
                    className={fieldClass}
                  />
                  {recipient.jobCreditsBalance != null && (
                    <p className="mt-1 text-[10px] font-semibold text-slate-500">
                      Current company job credits: {recipient.jobCreditsBalance}
                    </p>
                  )}
                  <p className="mt-0.5 text-[10px] font-semibold text-slate-600">
                    Adds to the recruiter's company balance. Amounts are additive.
                  </p>
                </div>
              )}
            </div>

            {closesAlert && (
              <p className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-[10px] font-semibold text-emerald-300">
                This also clears their "limit reached" flag and removes them from the Limits
                Exceeded list.
              </p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleClose}
                disabled={submitting}
                className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 text-sm font-black text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
              >
                {submitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Coins size={16} />
                )}
                Give Credits
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GiveCreditsModal;